import React, { useEffect, useMemo, useState } from "react";

const COLORS = {
  bg: "#080E1A",
  card: "#0D1525",
  cardHi: "#111B30",
  border: "#1A2540",
  borderHi: "#243154",
  accent: "#00D4B0",
  accentDim: "#00b095",
  text: "#EEF4FF",
  muted: "#8A9BC0",
  danger: "#FF5C6C",
};

const STYLES = ["Photorealistic","Cinematic","Luxury","Minimalist","Social Media","Editorial"];

const GPT_IMAGE_SIZES = [
  { label: "Square 1:1", value: "1024x1024" },
  { label: "Landscape 3:2", value: "1536x1024" },
  { label: "Portrait 2:3", value: "1024x1536" },
];
const GPT_IMAGE_QUALITIES = ["Auto", "High", "Medium", "Low"];

const MODELS = [
  { id: "gpt-image-2", label: "GPT-Image-2", description: "Latest — Best Quality", sizes: GPT_IMAGE_SIZES, qualities: GPT_IMAGE_QUALITIES },
  { id: "gpt-image-1", label: "GPT-Image-1", description: "High Quality", sizes: GPT_IMAGE_SIZES, qualities: GPT_IMAGE_QUALITIES },
  { id: "dall-e-2", label: "DALL-E 2", description: "Fast & Compatible", sizes: [{ label: "Square 256", value: "256x256" },{ label: "Square 512", value: "512x512" },{ label: "Square 1024", value: "1024x1024" }], qualities: null },
];

const VIDEO_MODELS = [
  { id: "fal-ai/kling-video/v2.1/standard/text-to-video", label: "Kling 2.1 Standard", desc: "Best value ~$0.35/5sec", duration: [5, 10] },
  { id: "fal-ai/kling-video/v2.1/pro/text-to-video", label: "Kling 2.1 Pro", desc: "High quality ~$0.70/5sec", duration: [5, 10] },
  { id: "fal-ai/veo3", label: "Veo 3 (Google)", desc: "Premium ~$0.50/5sec + audio", duration: [5, 8] },
];
const VIDEO_ASPECTS = [
  { label: "Landscape 16:9", value: "16:9" },
  { label: "Portrait 9:16", value: "9:16" },
  { label: "Square 1:1", value: "1:1" },
];

const DEFAULT_MODEL_ID = "gpt-image-2";
const getModel = (id) => MODELS.find((m) => m.id === id) || MODELS.find((m) => m.id === DEFAULT_MODEL_ID);

const NAV = [
  {
    section: "GENERATE",
    items: [
      { id: "generator", label: "Image Generator" },
      { id: "video", label: "Video Generator" },
      { id: "builder", label: "Prompt Builder" },
      { id: "batch", label: "Batch Generate" },
    ],
  },
  {
    section: "LIBRARY",
    items: [
      { id: "all", label: "All Images" },
      { id: "byClient", label: "By Client" },
      { id: "byStyle", label: "By Style" },
    ],
  },
  {
    section: "TOOLS",
    items: [
      { id: "enhancer", label: "Prompt Enhancer" },
      { id: "brand", label: "Brand Kit" },
    ],
  },
];

const LS = {
  openai: "sws_openai_key",
  anthropic: "sws_anthropic_key",
  fal: "sws_fal_key",
  library: "sws_library",
  brands: "sws_brands",
  activeBrand: "sws_active_brand",
  videos: "sws_videos",
};

const loadLS = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};
const saveLS = (key, val) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
};

const DALLE2_PROMPT_LIMIT = 1000;

async function callOpenAIImage({ apiKey, model, prompt, size, quality }) {
  const modelDef = getModel(model);
  const finalPrompt = modelDef.id === "dall-e-2" && prompt.length > DALLE2_PROMPT_LIMIT ? prompt.slice(0, DALLE2_PROMPT_LIMIT) : prompt;
  const body = { model: modelDef.id, prompt: finalPrompt, n: 1, size };
  if ((modelDef.id === "gpt-image-2" || modelDef.id === "gpt-image-1") && quality) {
    body.quality = quality.toLowerCase();
  } else if (modelDef.id === "dall-e-2") {
    body.response_format = "b64_json";
  }
  const res = await fetch("https://api.openai.com/v1/images/generations", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = data?.error || {};
    const detailParts = [err.message, err.type ? `type: ${err.type}` : null, err.code ? `code: ${err.code}` : null, err.param ? `param: ${err.param}` : null].filter(Boolean);
    const detail = detailParts.length ? detailParts.join(" — ") : data ? JSON.stringify(data) : res.statusText;
    const otherModels = MODELS.filter((m) => m.id !== modelDef.id).map((m) => m.label).join(" or ");
    throw new Error(`${modelDef.label} failed (HTTP ${res.status}): ${detail}. Try switching to ${otherModels} in the Model dropdown.`);
  }
  const item = data.data[0];
  const dataUrl = item.b64_json ? `data:image/png;base64,${item.b64_json}` : item.url;
  if (!dataUrl) throw new Error(`${modelDef.label} returned no image data.`);
  return { dataUrl, revisedPrompt: item.revised_prompt || finalPrompt };
}

async function callClaude({ apiKey, system, user }) {
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-api-key": apiKey, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
    body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1024, system, messages: [{ role: "user", content: user }] }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error?.message || "Anthropic request failed");
  return data.content?.[0]?.text || "";
}

function buildBrandSuffix(brand) {
  if (!brand) return "";
  const parts = [];
  if (brand.name) parts.push(`for the brand ${brand.name}`);
  if (brand.colors) parts.push(`incorporating brand colors ${brand.colors}`);
  if (brand.style) parts.push(`in the brand style: ${brand.style}`);
  if (brand.logo) parts.push(`logo description: ${brand.logo}`);
  return parts.length ? ` — ${parts.join("; ")}` : "";
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function App() {
  const [active, setActive] = useState("generator");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openaiKey, setOpenaiKey] = useState(() => loadLS(LS.openai, "") || "");
  const [anthropicKey, setAnthropicKey] = useState(() => loadLS(LS.anthropic, "") || "");
  const [falKey, setFalKey] = useState(() => loadLS(LS.fal, "") || "");
  const [library, setLibrary] = useState(() => loadLS(LS.library, []));
  const [brands, setBrands] = useState(() => loadLS(LS.brands, []));
  const [activeBrandId, setActiveBrandId] = useState(() => loadLS(LS.activeBrand, null));

  useEffect(() => saveLS(LS.openai, openaiKey), [openaiKey]);
  useEffect(() => saveLS(LS.anthropic, anthropicKey), [anthropicKey]);
  useEffect(() => saveLS(LS.fal, falKey), [falKey]);
  useEffect(() => saveLS(LS.library, library), [library]);
  useEffect(() => saveLS(LS.brands, brands), [brands]);
  useEffect(() => saveLS(LS.activeBrand, activeBrandId), [activeBrandId]);

  const activeBrand = useMemo(() => brands.find((b) => b.id === activeBrandId) || null, [brands, activeBrandId]);

  const saveToLibrary = (entry) => {
    setLibrary((prev) => [{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, createdAt: new Date().toISOString(), ...entry }, ...prev]);
  };
  const removeFromLibrary = (id) => setLibrary((prev) => prev.filter((x) => x.id !== id));
  const [pendingPrompt, setPendingPrompt] = useState("");
  const sendPromptToGenerator = (prompt) => { setPendingPrompt(prompt); setActive("generator"); };

  return (
    <div style={styles.app}>
      <Sidebar active={active} setActive={setActive} openSettings={() => setSettingsOpen(true)} />
      <main style={styles.main}>
        <div style={styles.mainInner}>
          {active === "generator" && <ImageGenerator openaiKey={openaiKey} activeBrand={activeBrand} saveToLibrary={saveToLibrary} brands={brands} activeBrandId={activeBrandId} setActiveBrandId={setActiveBrandId} pendingPrompt={pendingPrompt} clearPendingPrompt={() => setPendingPrompt("")} />}
          {active === "video" && <VideoGenerator falKey={falKey} />}
          {active === "builder" && <PromptBuilder anthropicKey={anthropicKey} sendToGenerator={sendPromptToGenerator} />}
          {active === "batch" && <BatchGenerate openaiKey={openaiKey} activeBrand={activeBrand} saveToLibrary={saveToLibrary} brands={brands} activeBrandId={activeBrandId} setActiveBrandId={setActiveBrandId} />}
          {active === "all" && <Library library={library} removeFromLibrary={removeFromLibrary} mode="all" />}
          {active === "byClient" && <Library library={library} removeFromLibrary={removeFromLibrary} mode="byClient" />}
          {active === "byStyle" && <Library library={library} removeFromLibrary={removeFromLibrary} mode="byStyle" />}
          {active === "enhancer" && <PromptEnhancer anthropicKey={anthropicKey} sendToGenerator={sendPromptToGenerator} />}
          {active === "brand" && <BrandKit brands={brands} setBrands={setBrands} activeBrandId={activeBrandId} setActiveBrandId={setActiveBrandId} />}
        </div>
      </main>
      {settingsOpen && <Settings onClose={() => setSettingsOpen(false)} openaiKey={openaiKey} setOpenaiKey={setOpenaiKey} anthropicKey={anthropicKey} setAnthropicKey={setAnthropicKey} falKey={falKey} setFalKey={setFalKey} />}
    </div>
  );
}

function VideoGenerator({ falKey }) {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState(VIDEO_MODELS[0].id);
  const [duration, setDuration] = useState(5);
  const [aspect, setAspect] = useState("16:9");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [videos, setVideos] = useState(() => loadLS(LS.videos, []));
  const [err, setErr] = useState("");

  useEffect(() => saveLS(LS.videos, videos), [videos]);
  const selectedModel = VIDEO_MODELS.find(m => m.id === model) || VIDEO_MODELS[0];

  const generate = async () => {
    if (!falKey) { setErr("Add your fal.ai API key in Settings first."); return; }
    if (!prompt.trim()) { setErr("Enter a prompt first."); return; }
    setErr(""); setLoading(true); setProgress("Submitting to fal.ai...");
    try {
      const submitRes = await fetch("https://queue.fal.run/" + model, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Key " + falKey },
        body: JSON.stringify({ prompt: prompt.trim(), duration, aspect_ratio: aspect })
      });
      if (!submitRes.ok) {
        const e = await submitRes.json().catch(() => ({}));
        throw new Error(e.detail || e.message || "Submission failed — check your fal.ai API key");
      }
      const { request_id } = await submitRes.json();
      setProgress("Video generating — this takes 1-3 minutes...");
      let result = null; let attempts = 0;
      while (!result && attempts < 72) {
        await new Promise(r => setTimeout(r, 5000));
        attempts++;
        setProgress("Generating... (" + (attempts * 5) + "s elapsed — keep this tab open)");
        const statusRes = await fetch("https://queue.fal.run/" + model + "/requests/" + request_id, {
          headers: { "Authorization": "Key " + falKey }
        });
        const status = await statusRes.json().catch(() => ({}));
        if (status.status === "COMPLETED") result = status;
        else if (status.status === "FAILED") throw new Error(status.error || "Generation failed");
      }
      if (!result) throw new Error("Timed out after 6 minutes — try a shorter duration or different model");
      const videoUrl = result.video?.url || result.output?.video?.url || result.data?.video?.url;
      if (!videoUrl) throw new Error("No video URL in response — try again");
      const newVideo = { id: Date.now(), url: videoUrl, prompt: prompt.trim(), model: selectedModel.label, duration, aspect, createdAt: new Date().toISOString() };
      setVideos(prev => [newVideo, ...prev].slice(0, 20));
      setProgress("");
    } catch(e) { setErr(e.message); }
    setLoading(false); setProgress("");
  };

  const estCost = (duration * (model.includes("pro") ? 0.14 : model.includes("veo") ? 0.10 : 0.07)).toFixed(2);

  return (
    <>
      <PageHeader title="Video Generator" subtitle="Generate AI video clips from text prompts using fal.ai. Videos are saved to your browser." />
      <div style={styles.twoCol}>
        <Card>
          <Field label="Prompt">
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="A cinematic shot of a small business owner opening their shop at sunrise, golden hour light, professional documentary style..." rows={5} style={styles.textarea} />
          </Field>
          <Field label="Model">
            <select value={model} onChange={e => { setModel(e.target.value); const m = VIDEO_MODELS.find(x=>x.id===e.target.value); setDuration(m?.duration[0]||5); }} style={styles.input}>
              {VIDEO_MODELS.map(m => <option key={m.id} value={m.id}>{m.label} — {m.desc}</option>)}
            </select>
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Field label="Aspect ratio">
              <select value={aspect} onChange={e => setAspect(e.target.value)} style={styles.input}>
                {VIDEO_ASPECTS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </Field>
            <Field label={`Duration: ${duration} seconds`}>
              <input type="range" min={selectedModel.duration[0]} max={selectedModel.duration[1]} step={1} value={duration} onChange={e => setDuration(Number(e.target.value))} style={{ width: "100%", accentColor: COLORS.accent, marginTop: 10 }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                <span>{selectedModel.duration[0]}s</span><span>{selectedModel.duration[1]}s</span>
              </div>
            </Field>
          </div>
          <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 14, padding: "8px 12px", background: COLORS.cardHi, borderRadius: 8 }}>
            Estimated cost: ~${estCost} · {duration}s clip · {selectedModel.label}
          </div>
          {err && <div style={styles.error}>{err}</div>}
          {loading ? (
            <div style={{ background: COLORS.cardHi, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 20, textAlign: "center" }}>
              <div style={styles.spinner} />
              <div style={{ color: COLORS.accent, fontSize: 14, marginBottom: 6, fontWeight: 600 }}>{progress}</div>
              <div style={{ fontSize: 12, color: COLORS.muted }}>Do not close this tab — video will be lost</div>
            </div>
          ) : (
            <PrimaryBtn onClick={generate} style={{ width: "100%" }}>Generate Video →</PrimaryBtn>
          )}
        </Card>

        <Card>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>Tips for great video</div>
          {[
            ["Shot type", "Lead with camera movement: 'A slow pan across...', 'Aerial shot of...', 'Close-up of...'"],
            ["Subject + action", "Be specific: 'A barista carefully pouring latte art' not just 'coffee'"],
            ["Setting + lighting", "Include environment: 'golden hour', 'neon-lit streets', 'bright modern office'"],
            ["Style", "Add aesthetic: 'cinematic', 'documentary style', '4K professional', 'social media vertical'"],
            ["Duration", "5s for social media clips, 8-10s for ads or product showcases"],
            ["Model choice", "Kling Standard for fast/budget, Kling Pro for quality, Veo 3 for audio+video"],
          ].map(([title, tip]) => (
            <div key={title} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.accent, marginBottom: 3 }}>{title}</div>
              <div style={{ fontSize: 12, color: COLORS.muted, lineHeight: 1.5 }}>{tip}</div>
            </div>
          ))}
        </Card>
      </div>

      {videos.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={styles.h2}>Generated Videos ({videos.length})</h2>
            <button onClick={() => { if(window.confirm("Clear all videos?")) setVideos([]); }} style={{ ...styles.btnGhost, fontSize: 12, padding: "8px 14px", color: COLORS.muted }}>Clear all</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
            {videos.map(v => (
              <Card key={v.id} style={{ padding: 0, overflow: "hidden" }}>
                <video src={v.url} controls style={{ width: "100%", display: "block", maxHeight: 220, background: "#000" }} />
                <div style={{ padding: 14 }}>
                  <div style={{ fontSize: 12, color: COLORS.text, marginBottom: 6, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{v.prompt}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                    <span style={{ fontSize: 10, color: COLORS.muted }}>{v.model} · {v.duration}s · {v.aspect}</span>
                    <a href={v.url} download={"video-" + v.id + ".mp4"} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: COLORS.accent, textDecoration: "none", fontWeight: 600 }}>⬇ Download</a>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {videos.length === 0 && !loading && (
        <div style={{ ...styles.previewEmpty, marginTop: 24, padding: "60px 24px" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎬</div>
          <div style={{ fontSize: 16, marginBottom: 6, color: COLORS.text }}>No videos yet</div>
          <div style={{ fontSize: 13 }}>Enter a prompt above and click Generate to create your first AI video clip</div>
        </div>
      )}
    </>
  );
}

function Sidebar({ active, setActive, openSettings }) {
  return (
    <aside style={styles.sidebar}>
      <div style={styles.brand}>
        <div style={styles.brandMark}><div style={styles.brandDot} /></div>
        <div>
          <div style={styles.brandTop}>Smart Web Advisors</div>
          <div style={styles.brandSub}>· Image Studio</div>
        </div>
      </div>
      <nav style={styles.nav}>
        {NAV.map((group) => (
          <div key={group.section} style={{ marginBottom: 22 }}>
            <div style={styles.navSection}>{group.section}</div>
            {group.items.map((item) => {
              const isActive = active === item.id;
              return (
                <button key={item.id} onClick={() => setActive(item.id)} style={{ ...styles.navItem, ...(isActive ? styles.navItemActive : {}) }}>
                  <span style={{ ...styles.navItemBar, background: isActive ? COLORS.accent : "transparent" }} />
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
      <div style={styles.sidebarFooter}>
        <button onClick={openSettings} style={styles.settingsBtn}>⚙ &nbsp;Settings</button>
      </div>
    </aside>
  );
}

function PageHeader({ title, subtitle, right }) {
  return (
    <header style={styles.pageHeader}>
      <div>
        <h1 style={styles.pageTitle}>{title}</h1>
        {subtitle && <p style={styles.pageSubtitle}>{subtitle}</p>}
      </div>
      {right}
    </header>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      {children}
      {hint && <div style={styles.hint}>{hint}</div>}
    </div>
  );
}

function Card({ children, style }) {
  return <div style={{ ...styles.card, ...style }}>{children}</div>;
}

function PrimaryBtn({ children, disabled, onClick, style }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...styles.btnPrimary, ...(disabled ? styles.btnPrimaryDisabled : {}), ...style }}>
      {children}
    </button>
  );
}

function GhostBtn({ children, onClick, style, disabled }) {
  return <button onClick={onClick} disabled={disabled} style={{ ...styles.btnGhost, ...style }}>{children}</button>;
}

function Pill({ active, onClick, children }) {
  return <button onClick={onClick} style={{ ...styles.pill, ...(active ? styles.pillActive : {}) }}>{children}</button>;
}

function ErrorBanner({ msg }) {
  if (!msg) return null;
  return <div style={styles.error}>{msg}</div>;
}

function PromptCounter({ length, max }) {
  const over = length > max;
  return (
    <div style={{ marginTop: 6, fontSize: 12, color: over ? COLORS.danger : COLORS.muted, fontWeight: over ? 600 : 400 }}>
      {length} / {max} characters{over && ` — will be truncated to ${max}`}
    </div>
  );
}

function BrandSelector({ brands, activeBrandId, setActiveBrandId }) {
  if (!brands.length) return null;
  return (
    <Field label="Active Brand Kit" hint="Brand details are automatically blended into the prompt.">
      <select value={activeBrandId || ""} onChange={(e) => setActiveBrandId(e.target.value || null)} style={styles.input}>
        <option value="">— None —</option>
        {brands.map((b) => <option key={b.id} value={b.id}>{b.name || "Untitled brand"}</option>)}
      </select>
    </Field>
  );
}

function ImageGenerator({ openaiKey, activeBrand, saveToLibrary, brands, activeBrandId, setActiveBrandId, pendingPrompt, clearPendingPrompt }) {
  const [idea, setIdea] = useState("");
  const [style, setStyle] = useState(STYLES[0]);
  const [modelId, setModelId] = useState(DEFAULT_MODEL_ID);
  const model = getModel(modelId);
  const [aspect, setAspect] = useState(model.sizes[0]);
  const [quality, setQuality] = useState(model.qualities ? model.qualities[0] : null);
  const [client, setClient] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => { if (pendingPrompt) { setIdea(pendingPrompt); clearPendingPrompt(); } }, [pendingPrompt, clearPendingPrompt]);

  const onModelChange = (nextId) => { const next = getModel(nextId); setModelId(next.id); setAspect(next.sizes[0]); setQuality(next.qualities ? next.qualities[0] : null); };
  const buildFullPrompt = () => `${idea.trim()} — ${style} style${buildBrandSuffix(activeBrand)}`.trim();

  const onGenerate = async () => {
    setErr("");
    if (!openaiKey) { setErr("Add your OpenAI API key in Settings first."); return; }
    if (!idea.trim()) { setErr("Enter an idea to generate."); return; }
    setLoading(true); setResult(null);
    try {
      const prompt = buildFullPrompt();
      const { dataUrl, revisedPrompt } = await callOpenAIImage({ apiKey: openaiKey, model: model.id, prompt, size: aspect.value, quality });
      const entry = { dataUrl, prompt, revisedPrompt, style, client: client.trim(), aspect: aspect.label, quality: quality || "—", model: model.id, modelLabel: model.label };
      setResult(entry);
      saveToLibrary(entry);
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title="Image Generator" subtitle="Generate a single image with the OpenAI model of your choice." />
      <div style={styles.twoCol}>
        <Card>
          <Field label="Idea">
            <textarea value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="A weathered sailor at sunrise, mist on the harbor…" rows={4} style={styles.textarea} />
            {model.id === "dall-e-2" && <PromptCounter length={buildFullPrompt().length} max={DALLE2_PROMPT_LIMIT} />}
          </Field>
          <Field label="Model" hint={`${model.label} — ${model.description}`}>
            <select value={model.id} onChange={(e) => onModelChange(e.target.value)} style={styles.input}>
              {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label} ({m.description})</option>)}
            </select>
          </Field>
          <Field label="Style">
            <div style={styles.pillRow}>{STYLES.map((s) => <Pill key={s} active={style === s} onClick={() => setStyle(s)}>{s}</Pill>)}</div>
          </Field>
          <Field label="Size">
            <div style={styles.pillRow}>{model.sizes.map((a) => <Pill key={a.label} active={aspect.label === a.label} onClick={() => setAspect(a)}>{a.label}</Pill>)}</div>
          </Field>
          {model.qualities && (
            <Field label="Quality">
              <div style={styles.pillRow}>{model.qualities.map((q) => <Pill key={q} active={quality === q} onClick={() => setQuality(q)}>{q}</Pill>)}</div>
            </Field>
          )}
          <Field label="Client (optional)">
            <input value={client} onChange={(e) => setClient(e.target.value)} placeholder="e.g. Cobalt Coffee" style={styles.input} />
          </Field>
          <BrandSelector brands={brands} activeBrandId={activeBrandId} setActiveBrandId={setActiveBrandId} />
          <ErrorBanner msg={err} />
          <PrimaryBtn onClick={onGenerate} disabled={loading}>{loading ? "Generating…" : "Generate Image"}</PrimaryBtn>
        </Card>
        <Card style={{ minHeight: 480 }}>
          <div style={styles.label}>Preview</div>
          {!result && !loading && <div style={styles.previewEmpty}>Your generated image will appear here.</div>}
          {loading && <div style={styles.previewEmpty}><div style={styles.spinner} /><div style={{ marginTop: 14, color: COLORS.muted }}>Painting pixels…</div></div>}
          {result && (
            <div>
              <img src={result.dataUrl} alt="Generated" style={styles.previewImg} />
              <div style={styles.previewMeta}>
                <div><span style={styles.metaKey}>Model:</span> {result.modelLabel || result.model}</div>
                <div><span style={styles.metaKey}>Style:</span> {result.style}</div>
                <div><span style={styles.metaKey}>Size:</span> {result.aspect}</div>
                <div><span style={styles.metaKey}>Quality:</span> {result.quality}</div>
                {result.client && <div><span style={styles.metaKey}>Client:</span> {result.client}</div>}
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <PrimaryBtn onClick={() => downloadDataUrl(result.dataUrl, `sws-${Date.now()}.png`)}>⬇ Download</PrimaryBtn>
                <GhostBtn onClick={() => navigator.clipboard.writeText(result.prompt)}>Copy Prompt</GhostBtn>
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function PromptBuilder({ anthropicKey, sendToGenerator }) {
  const [subject, setSubject] = useState("");
  const [setting, setSetting] = useState("");
  const [mood, setMood] = useState("");
  const [lighting, setLighting] = useState("");
  const [style, setStyle] = useState("");
  const [palette, setPalette] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [built, setBuilt] = useState("");

  const onBuild = async () => {
    setErr("");
    if (!anthropicKey) { setErr("Add your Anthropic API key in Settings first."); return; }
    const filled = [subject, setting, mood, lighting, style, palette].some((x) => x.trim());
    if (!filled) { setErr("Fill in at least one field."); return; }
    setLoading(true);
    try {
      const user = `Build a single, professional image-generation prompt for an AI image model from these inputs. Return ONLY the prompt, no preamble, no quotes.\n\nSubject: ${subject || "(unspecified)"}\nSetting: ${setting || "(unspecified)"}\nMood: ${mood || "(unspecified)"}\nLighting: ${lighting || "(unspecified)"}\nStyle: ${style || "(unspecified)"}\nColor Palette: ${palette || "(unspecified)"}`;
      const text = await callClaude({ apiKey: anthropicKey, system: "You are an expert prompt engineer for AI image generation. Produce vivid, specific, photographically detailed prompts in a single paragraph.", user });
      setBuilt(text.trim());
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title="Prompt Builder" subtitle="Compose a professional image prompt from structured inputs." />
      <div style={styles.twoCol}>
        <Card>
          <Field label="Subject"><input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A barista pouring latte art" style={styles.input} /></Field>
          <Field label="Setting"><input value={setting} onChange={(e) => setSetting(e.target.value)} placeholder="An open-air cafe in Lisbon" style={styles.input} /></Field>
          <Field label="Mood"><input value={mood} onChange={(e) => setMood(e.target.value)} placeholder="Warm, unhurried, contemplative" style={styles.input} /></Field>
          <Field label="Lighting"><input value={lighting} onChange={(e) => setLighting(e.target.value)} placeholder="Soft morning sun through linen curtains" style={styles.input} /></Field>
          <Field label="Style"><input value={style} onChange={(e) => setStyle(e.target.value)} placeholder="Editorial photography, 35mm film" style={styles.input} /></Field>
          <Field label="Color Palette"><input value={palette} onChange={(e) => setPalette(e.target.value)} placeholder="Cream, terracotta, deep olive" style={styles.input} /></Field>
          <ErrorBanner msg={err} />
          <PrimaryBtn onClick={onBuild} disabled={loading}>{loading ? "Building…" : "Build Prompt with Claude"}</PrimaryBtn>
        </Card>
        <Card>
          <Field label="Generated Prompt">
            <textarea value={built} onChange={(e) => setBuilt(e.target.value)} placeholder="Your professional prompt will appear here…" rows={14} style={styles.textarea} />
          </Field>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <GhostBtn disabled={!built} onClick={() => navigator.clipboard.writeText(built)}>Copy</GhostBtn>
            <PrimaryBtn disabled={!built} onClick={() => sendToGenerator(built)}>Send to Generator →</PrimaryBtn>
          </div>
        </Card>
      </div>
    </>
  );
}

function BatchGenerate({ openaiKey, activeBrand, saveToLibrary, brands, activeBrandId, setActiveBrandId }) {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState(STYLES[0]);
  const [modelId, setModelId] = useState(DEFAULT_MODEL_ID);
  const model = getModel(modelId);
  const [aspect, setAspect] = useState(model.sizes[0]);
  const [quality, setQuality] = useState(model.qualities ? model.qualities[0] : null);
  const [count, setCount] = useState(2);
  const [client, setClient] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [results, setResults] = useState([]);

  const onModelChange = (nextId) => { const next = getModel(nextId); setModelId(next.id); setAspect(next.sizes[0]); setQuality(next.qualities ? next.qualities[0] : null); };

  const onGenerate = async () => {
    setErr("");
    if (!openaiKey) { setErr("Add your OpenAI API key in Settings first."); return; }
    if (!prompt.trim()) { setErr("Enter a prompt to batch generate."); return; }
    const fullPrompt = `${prompt.trim()} — ${style} style${buildBrandSuffix(activeBrand)}`;
    setLoading(true); setResults([]);
    try {
      const promises = Array.from({ length: count }).map(() =>
        callOpenAIImage({ apiKey: openaiKey, model: model.id, prompt: fullPrompt, size: aspect.value, quality }).then((r) => ({ ok: true, ...r })).catch((e) => ({ ok: false, error: e.message || String(e) }))
      );
      const out = await Promise.all(promises);
      const finalResults = out.map((r) => {
        if (!r.ok) return r;
        const entry = { dataUrl: r.dataUrl, prompt: fullPrompt, revisedPrompt: r.revisedPrompt, style, client: client.trim(), aspect: aspect.label, quality: quality || "—", model: model.id, modelLabel: model.label };
        saveToLibrary(entry);
        return { ok: true, entry };
      });
      setResults(finalResults);
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title="Batch Generate" subtitle="Generate 2–4 variations of the same prompt at once." />
      <Card>
        <Field label="Prompt">
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="Describe what you want to see…" style={styles.textarea} />
          {model.id === "dall-e-2" && <PromptCounter length={`${prompt.trim()} — ${style} style${buildBrandSuffix(activeBrand)}`.length} max={DALLE2_PROMPT_LIMIT} />}
        </Field>
        <div style={styles.row3}>
          <Field label="Model" hint={`${model.label} — ${model.description}`}>
            <select value={model.id} onChange={(e) => onModelChange(e.target.value)} style={styles.input}>
              {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label} ({m.description})</option>)}
            </select>
          </Field>
          <Field label="Style">
            <select value={style} onChange={(e) => setStyle(e.target.value)} style={styles.input}>
              {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Size">
            <select value={aspect.label} onChange={(e) => setAspect(model.sizes.find((a) => a.label === e.target.value))} style={styles.input}>
              {model.sizes.map((a) => <option key={a.label} value={a.label}>{a.label}</option>)}
            </select>
          </Field>
        </div>
        {model.qualities && (
          <div style={styles.row3}>
            <Field label="Quality">
              <select value={quality || ""} onChange={(e) => setQuality(e.target.value)} style={styles.input}>
                {model.qualities.map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </Field>
          </div>
        )}
        <div style={styles.row3}>
          <Field label="Variations">
            <div style={styles.pillRow}>{[2, 3, 4].map((n) => <Pill key={n} active={count === n} onClick={() => setCount(n)}>{n}</Pill>)}</div>
          </Field>
          <Field label="Client (optional)">
            <input value={client} onChange={(e) => setClient(e.target.value)} placeholder="e.g. Aurora Realty" style={styles.input} />
          </Field>
          <BrandSelector brands={brands} activeBrandId={activeBrandId} setActiveBrandId={setActiveBrandId} />
        </div>
        <ErrorBanner msg={err} />
        <PrimaryBtn onClick={onGenerate} disabled={loading}>{loading ? `Generating ${count}…` : `Generate ${count} Variations`}</PrimaryBtn>
      </Card>
      {(loading || results.length > 0) && (
        <div style={{ marginTop: 24 }}>
          <h2 style={styles.h2}>Results</h2>
          <div style={styles.grid}>
            {loading && Array.from({ length: count }).map((_, i) => <Card key={i} style={{ textAlign: "center" }}><div style={styles.spinner} /><div style={{ marginTop: 12, color: COLORS.muted }}>Variation {i + 1}</div></Card>)}
            {!loading && results.map((r, i) => (
              <Card key={i}>
                {r.ok ? (<><img src={r.entry.dataUrl} alt={`Variation ${i + 1}`} style={styles.gridImg} /><div style={{ marginTop: 12 }}><PrimaryBtn onClick={() => downloadDataUrl(r.entry.dataUrl, `sws-batch-${i + 1}-${Date.now()}.png`)} style={{ width: "100%" }}>⬇ Download</PrimaryBtn></div></>) : (<div style={styles.error}>{r.error}</div>)}
              </Card>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function PromptEnhancer({ anthropicKey, sendToGenerator }) {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onEnhance = async () => {
    setErr("");
    if (!anthropicKey) { setErr("Add your Anthropic API key in Settings first."); return; }
    if (!input.trim()) { setErr("Paste a prompt to enhance."); return; }
    setLoading(true);
    try {
      const text = await callClaude({ apiKey: anthropicKey, system: "You are an expert prompt engineer optimizing prompts for modern AI image models. Rewrite the user's basic prompt into a single, detailed, professional image-generation prompt. Include subject, setting, lighting, mood, style, lens/medium, and palette details where useful. Return ONLY the rewritten prompt — no preamble, no quotes, no formatting.", user: input.trim() });
      setOutput(text.trim());
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title="Prompt Enhancer" subtitle="Turn a one-liner into a polished, image-generation-ready prompt." />
      <div style={styles.twoCol}>
        <Card>
          <Field label="Basic Prompt"><textarea value={input} onChange={(e) => setInput(e.target.value)} rows={10} placeholder="A coffee shop on a rainy day…" style={styles.textarea} /></Field>
          <ErrorBanner msg={err} />
          <PrimaryBtn onClick={onEnhance} disabled={loading}>{loading ? "Enhancing…" : "Enhance with Claude"}</PrimaryBtn>
        </Card>
        <Card>
          <Field label="Enhanced Prompt"><textarea value={output} onChange={(e) => setOutput(e.target.value)} rows={14} placeholder="Your enhanced prompt will appear here…" style={styles.textarea} /></Field>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <GhostBtn disabled={!output} onClick={() => navigator.clipboard.writeText(output)}>Copy</GhostBtn>
            <PrimaryBtn disabled={!output} onClick={() => sendToGenerator(output)}>Send to Generator →</PrimaryBtn>
          </div>
        </Card>
      </div>
    </>
  );
}

function BrandKit({ brands, setBrands, activeBrandId, setActiveBrandId }) {
  const [editing, setEditing] = useState(null);

  const newBrand = () => setEditing({ id: `brand-${Date.now()}`, name: "", colors: "", style: "", logo: "", isNew: true });

  const saveBrand = () => {
    if (!editing) return;
    const clean = { ...editing }; delete clean.isNew;
    setBrands((prev) => { const idx = prev.findIndex((b) => b.id === clean.id); if (idx === -1) return [...prev, clean]; const copy = [...prev]; copy[idx] = clean; return copy; });
    setEditing(null);
  };

  const deleteBrand = (id) => { setBrands((prev) => prev.filter((b) => b.id !== id)); if (activeBrandId === id) setActiveBrandId(null); };

  return (
    <>
      <PageHeader title="Brand Kit" subtitle="Store client brand details that auto-merge into prompts." right={<PrimaryBtn onClick={newBrand}>+ New Brand</PrimaryBtn>} />
      {brands.length === 0 && !editing && <Card><div style={styles.previewEmpty}>No brand kits yet. Create one to start blending brand details into your prompts.</div></Card>}
      {editing && (
        <Card style={{ marginBottom: 20, borderColor: COLORS.accent }}>
          <h2 style={styles.h2}>{editing.isNew ? "New Brand" : "Edit Brand"}</h2>
          <Field label="Brand / Client Name"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Cobalt Coffee" style={styles.input} /></Field>
          <Field label="Brand Colors"><input value={editing.colors} onChange={(e) => setEditing({ ...editing, colors: e.target.value })} placeholder="Deep navy #0B2540, warm cream #F5EFE3, brass #B68F3F" style={styles.input} /></Field>
          <Field label="Style Preferences"><textarea value={editing.style} onChange={(e) => setEditing({ ...editing, style: e.target.value })} rows={3} placeholder="Editorial, restrained, lots of negative space, mid-century inspired…" style={styles.textarea} /></Field>
          <Field label="Logo Description"><textarea value={editing.logo} onChange={(e) => setEditing({ ...editing, logo: e.target.value })} rows={2} placeholder="A minimal wordmark in serif type, sometimes paired with a stylized coffee leaf icon." style={styles.textarea} /></Field>
          <div style={{ display: "flex", gap: 10 }}><PrimaryBtn onClick={saveBrand}>Save Brand</PrimaryBtn><GhostBtn onClick={() => setEditing(null)}>Cancel</GhostBtn></div>
        </Card>
      )}
      <div style={styles.grid}>
        {brands.map((b) => (
          <Card key={b.id}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ ...styles.h3, margin: 0 }}>{b.name || "Untitled"}</h3>
              {activeBrandId === b.id ? <span style={styles.activeChip}>ACTIVE</span> : <GhostBtn onClick={() => setActiveBrandId(b.id)} style={{ padding: "6px 12px", fontSize: 12 }}>Set Active</GhostBtn>}
            </div>
            <div style={styles.brandMetaRow}><span style={styles.metaKey}>Colors:</span> {b.colors || <span style={styles.muted}>—</span>}</div>
            <div style={styles.brandMetaRow}><span style={styles.metaKey}>Style:</span> {b.style || <span style={styles.muted}>—</span>}</div>
            <div style={styles.brandMetaRow}><span style={styles.metaKey}>Logo:</span> {b.logo || <span style={styles.muted}>—</span>}</div>
            <div style={{ display: "flex", gap: 10, marginTop: 14, borderTop: `1px solid ${COLORS.border}`, paddingTop: 14 }}>
              <GhostBtn onClick={() => setEditing({ ...b })}>Edit</GhostBtn>
              <GhostBtn onClick={() => deleteBrand(b.id)} style={{ color: COLORS.danger, borderColor: "#3a1f2a" }}>Delete</GhostBtn>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

function Library({ library, removeFromLibrary, mode }) {
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);

  const groups = useMemo(() => {
    if (mode === "all") {
      const items = filter ? library.filter((x) => (x.prompt || "").toLowerCase().includes(filter.toLowerCase()) || (x.client || "").toLowerCase().includes(filter.toLowerCase()) || (x.style || "").toLowerCase().includes(filter.toLowerCase())) : library;
      return [{ key: "all", title: `All Images (${items.length})`, items }];
    }
    const key = mode === "byClient" ? "client" : "style";
    const map = new Map();
    library.forEach((x) => { const k = (x[key] || "Unassigned").trim() || "Unassigned"; if (!map.has(k)) map.set(k, []); map.get(k).push(x); });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([k, items]) => ({ key: k, title: `${k} (${items.length})`, items }));
  }, [library, mode, filter]);

  const empty = library.length === 0;
  const title = mode === "byClient" ? "Library · By Client" : mode === "byStyle" ? "Library · By Style" : "All Images";
  const subtitle = mode === "all" ? "Every image you've generated, newest first." : mode === "byClient" ? "Images grouped by client." : "Images grouped by style.";

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} right={mode === "all" && <input placeholder="Search prompt, client, style…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ ...styles.input, width: 280, marginBottom: 0 }} />} />
      {empty && <Card><div style={styles.previewEmpty}>Your library is empty. Generate some images to populate it.</div></Card>}
      {!empty && groups.map((g) => (
        <div key={g.key} style={{ marginBottom: 28 }}>
          {mode !== "all" && <h2 style={styles.h2}>{g.title}</h2>}
          <div style={styles.grid}>
            {g.items.map((item) => (
              <Card key={item.id} style={{ padding: 12 }}>
                <button onClick={() => setOpen(item)} style={styles.imgBtn} title="View"><img src={item.dataUrl} alt={item.prompt} style={styles.gridImg} /></button>
                <div style={styles.libMeta}>
                  <div style={styles.libMetaRow}><span style={styles.metaKey}>Model:</span> {item.modelLabel || (item.model ? item.model : <span style={styles.muted}>—</span>)}</div>
                  <div style={styles.libMetaRow}><span style={styles.metaKey}>Client:</span> {item.client || <span style={styles.muted}>—</span>}</div>
                  <div style={styles.libMetaRow}><span style={styles.metaKey}>Style:</span> {item.style}</div>
                  <div style={styles.libMetaRow}><span style={styles.metaKey}>Date:</span> {new Date(item.createdAt).toLocaleDateString()}</div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <PrimaryBtn onClick={() => downloadDataUrl(item.dataUrl, `sws-${item.id}.png`)} style={{ flex: 1, fontSize: 13, padding: "10px 12px" }}>⬇ Download</PrimaryBtn>
                  <GhostBtn onClick={() => removeFromLibrary(item.id)} style={{ color: COLORS.danger, borderColor: "#3a1f2a", padding: "10px 12px", fontSize: 13 }}>✕</GhostBtn>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
      {open && (
        <Modal onClose={() => setOpen(null)}>
          <img src={open.dataUrl} alt={open.prompt} style={{ width: "100%", borderRadius: 12 }} />
          <div style={{ marginTop: 16 }}>
            <div style={styles.libMetaRow}><span style={styles.metaKey}>Prompt:</span> {open.prompt}</div>
            {open.revisedPrompt && open.revisedPrompt !== open.prompt && <div style={styles.libMetaRow}><span style={styles.metaKey}>Revised:</span> {open.revisedPrompt}</div>}
            <div style={styles.libMetaRow}><span style={styles.metaKey}>Model:</span> {open.modelLabel || (open.model ? open.model : <span style={styles.muted}>—</span>)}</div>
            <div style={styles.libMetaRow}><span style={styles.metaKey}>Style:</span> {open.style}</div>
            <div style={styles.libMetaRow}><span style={styles.metaKey}>Size:</span> {open.aspect}</div>
            <div style={styles.libMetaRow}><span style={styles.metaKey}>Quality:</span> {open.quality}</div>
            {open.client && <div style={styles.libMetaRow}><span style={styles.metaKey}>Client:</span> {open.client}</div>}
            <div style={styles.libMetaRow}><span style={styles.metaKey}>Created:</span> {new Date(open.createdAt).toLocaleString()}</div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <PrimaryBtn onClick={() => downloadDataUrl(open.dataUrl, `sws-${open.id}.png`)}>⬇ Download</PrimaryBtn>
            <GhostBtn onClick={() => navigator.clipboard.writeText(open.prompt)}>Copy Prompt</GhostBtn>
          </div>
        </Modal>
      )}
    </>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={styles.modalClose}>✕</button>
        {children}
      </div>
    </div>
  );
}

function Settings({ onClose, openaiKey, setOpenaiKey, anthropicKey, setAnthropicKey, falKey, setFalKey }) {
  const [showOA, setShowOA] = useState(false);
  const [showAN, setShowAN] = useState(false);
  const [showFAL, setShowFAL] = useState(false);
  return (
    <Modal onClose={onClose}>
      <h2 style={{ ...styles.h2, marginTop: 0 }}>Settings</h2>
      <p style={{ color: COLORS.muted, marginTop: -8 }}>API keys are stored only in your browser's localStorage.</p>
      <Field label="OpenAI API Key" hint="Used for GPT-Image-2, GPT-Image-1, and DALL-E 2 image generation.">
        <div style={styles.keyRow}>
          <input value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)} type={showOA ? "text" : "password"} placeholder="sk-…" style={{ ...styles.input, marginBottom: 0, flex: 1 }} />
          <GhostBtn onClick={() => setShowOA((v) => !v)} style={{ padding: "10px 14px" }}>{showOA ? "Hide" : "Show"}</GhostBtn>
        </div>
      </Field>
      <Field label="Anthropic API Key" hint="Used for Claude prompt building and enhancement.">
        <div style={styles.keyRow}>
          <input value={anthropicKey} onChange={(e) => setAnthropicKey(e.target.value)} type={showAN ? "text" : "password"} placeholder="sk-ant-…" style={{ ...styles.input, marginBottom: 0, flex: 1 }} />
          <GhostBtn onClick={() => setShowAN((v) => !v)} style={{ padding: "10px 14px" }}>{showAN ? "Hide" : "Show"}</GhostBtn>
        </div>
      </Field>
      <Field label="fal.ai API Key" hint="Used for AI video generation (Kling, Veo 3). Get your key at fal.ai/dashboard.">
        <div style={styles.keyRow}>
          <input value={falKey || ""} onChange={(e) => setFalKey(e.target.value)} type={showFAL ? "text" : "password"} placeholder="key-…" style={{ ...styles.input, marginBottom: 0, flex: 1 }} />
          <GhostBtn onClick={() => setShowFAL((v) => !v)} style={{ padding: "10px 14px" }}>{showFAL ? "Hide" : "Show"}</GhostBtn>
        </div>
      </Field>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <PrimaryBtn onClick={onClose}>Done</PrimaryBtn>
      </div>
    </Modal>
  );
}

const styles = {
  app: { display: "flex", minHeight: "100vh", background: COLORS.bg, color: COLORS.text },
  sidebar: { position: "fixed", top: 0, left: 0, bottom: 0, width: 264, background: "#06101F", borderRight: `1px solid ${COLORS.border}`, padding: "24px 18px 18px", display: "flex", flexDirection: "column", overflow: "auto" },
  brand: { display: "flex", alignItems: "center", gap: 12, paddingBottom: 22, marginBottom: 18, borderBottom: `1px solid ${COLORS.border}` },
  brandMark: { width: 38, height: 38, borderRadius: 10, background: `linear-gradient(135deg, ${COLORS.accent} 0%, #0099d4 100%)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  brandDot: { width: 12, height: 12, borderRadius: "50%", background: COLORS.bg },
  brandTop: { fontFamily: '"Bricolage Grotesque", serif', fontWeight: 700, fontSize: 15, color: COLORS.text, lineHeight: 1.15, letterSpacing: -0.2 },
  brandSub: { fontSize: 12, color: COLORS.muted, marginTop: 2 },
  nav: { flex: 1 },
  navSection: { fontSize: 11, letterSpacing: 1.4, color: COLORS.muted, padding: "0 10px 8px", fontWeight: 600 },
  navItem: { position: "relative", width: "100%", textAlign: "left", background: "transparent", color: COLORS.text, border: "none", padding: "10px 14px", borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer", marginBottom: 2, transition: "background 0.15s" },
  navItemActive: { background: COLORS.card, color: COLORS.text },
  navItemBar: { position: "absolute", left: 0, top: 8, bottom: 8, width: 3, borderRadius: 2 },
  sidebarFooter: { borderTop: `1px solid ${COLORS.border}`, paddingTop: 14 },
  settingsBtn: { width: "100%", background: "transparent", color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 14, fontWeight: 500, cursor: "pointer" },
  main: { marginLeft: 264, flex: 1, padding: "32px 40px 56px" },
  mainInner: { maxWidth: 1200, margin: "0 auto" },
  pageHeader: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, marginBottom: 26 },
  pageTitle: { fontFamily: '"Bricolage Grotesque", serif', fontWeight: 700, fontSize: 34, margin: 0, letterSpacing: -0.6 },
  pageSubtitle: { color: COLORS.muted, margin: "6px 0 0", fontSize: 14 },
  card: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 22 },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 },
  row3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 18 },
  field: { marginBottom: 16 },
  label: { display: "block", fontSize: 12, color: COLORS.muted, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 8 },
  hint: { fontSize: 12, color: COLORS.muted, marginTop: 6 },
  input: { width: "100%", background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none" },
  textarea: { width: "100%", background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "12px", fontSize: 14, outline: "none", resize: "vertical", minHeight: 90 },
  pillRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  pill: { background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 999, padding: "8px 14px", fontSize: 13, cursor: "pointer", fontWeight: 500 },
  pillActive: { background: COLORS.accent, color: "#001a16", borderColor: COLORS.accent, fontWeight: 600 },
  btnPrimary: { background: COLORS.accent, color: "#001a16", border: "none", borderRadius: 8, padding: "12px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  btnPrimaryDisabled: { background: "#11392F", color: "#4f7a72", cursor: "not-allowed" },
  btnGhost: { background: "transparent", color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "12px 18px", fontSize: 14, fontWeight: 500, cursor: "pointer" },
  error: { background: "#2a131a", border: "1px solid #4a1f2a", color: "#ffb3bd", padding: "10px 12px", borderRadius: 8, fontSize: 13, margin: "8px 0 14px" },
  previewEmpty: { border: `1px dashed ${COLORS.border}`, borderRadius: 12, padding: 36, textAlign: "center", color: COLORS.muted, fontSize: 14, background: "rgba(255,255,255,0.01)" },
  previewImg: { width: "100%", borderRadius: 10, display: "block" },
  previewMeta: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 14, fontSize: 13, color: COLORS.text },
  metaKey: { color: COLORS.muted, marginRight: 4 },
  muted: { color: COLORS.muted },
  gridImg: { width: "100%", borderRadius: 8, display: "block", aspectRatio: "1 / 1", objectFit: "cover" },
  imgBtn: { border: "none", background: "transparent", padding: 0, cursor: "zoom-in", width: "100%" },
  libMeta: { marginTop: 10, fontSize: 12.5 },
  libMetaRow: { marginBottom: 4, color: COLORS.text },
  h2: { fontFamily: '"Bricolage Grotesque", serif', fontWeight: 700, fontSize: 22, margin: "0 0 14px", letterSpacing: -0.3 },
  h3: { fontFamily: '"Bricolage Grotesque", serif', fontWeight: 600, fontSize: 17, letterSpacing: -0.2 },
  brandMetaRow: { fontSize: 13, color: COLORS.text, marginTop: 8, lineHeight: 1.5 },
  activeChip: { background: COLORS.accent, color: "#001a16", fontSize: 11, fontWeight: 700, letterSpacing: 0.8, padding: "4px 8px", borderRadius: 999 },
  modalBackdrop: { position: "fixed", inset: 0, background: "rgba(4, 8, 16, 0.78)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 24 },
  modal: { position: "relative", background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 26, maxWidth: 720, width: "100%", maxHeight: "92vh", overflow: "auto" },
  modalClose: { position: "absolute", top: 14, right: 14, background: "transparent", color: COLORS.muted, border: "none", fontSize: 18, cursor: "pointer" },
  keyRow: { display: "flex", gap: 8, alignItems: "stretch" },
  spinner: { width: 32, height: 32, border: `3px solid ${COLORS.border}`, borderTopColor: COLORS.accent, borderRadius: "50%", margin: "24px auto", animation: "sws-spin 0.9s linear infinite" },
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes sws-spin { to { transform: rotate(360deg); } }
  input::placeholder, textarea::placeholder { color: #5a6c8a; }
  input:focus, textarea:focus, select:focus { border-color: ${COLORS.accent} !important; }
  button:hover:not(:disabled) { filter: brightness(1.08); }
`;
if (typeof document !== "undefined" && !document.getElementById("sws-anim")) {
  styleSheet.id = "sws-anim";
  document.head.appendChild(styleSheet);
}

export default App;
